import axios, { AxiosResponse } from 'axios';
import { AdminContentManagement } from './admin-content-management.type';
import CmsList = AdminContentManagement.CmsList;
import ContentTypeData = AdminContentManagement.ContentTypeData;
import {
    EventSection,
    EventType,
    logFunction,
    sectionCreateConfig,
    sectionDeleteConfig,
    sectionEditConfig,
    sectionViewConfig,
} from '../../services/logs';
import MoveData = AdminContentManagement.MoveData;

/**
 * Список материалов типа контента (только опубликованные)
 */
export const getContentListByType = (
    serviceUrl: string,
    values: any,
    size = 20,
    from = 0
): Promise<AxiosResponse<CmsList<ContentTypeData>>> => {
    return logFunction({
        config: {
            description: 'Список материалов типа контента (только опубликованные)',
            section: EventSection.ADM_EVENTS,
            type: EventType.VIEW,
        },
        wrapperFunction() {
            return axios.get<CmsList<ContentTypeData>>(serviceUrl, {
                params: { values, size, from, lang: 'ru' },
            });
        },
    });
};

/**
 * Получение материала по ID
 */
export const getContentById = (serviceUrl: string, id: string): Promise<AxiosResponse<any>> => {
    return logFunction({
        config: {
            description: 'Получение материала по ID',
            section: EventSection.ADM_EVENTS,
            type: EventType.VIEW,
        },
        wrapperFunction() {
            return axios.get<any>(serviceUrl, { params: { id } });
        },
    });
};

/**
 * Создание материала контента
 */
export const createContentByType = (data: any): Promise<AxiosResponse<any>> => {
    return logFunction({
        config: { ...sectionCreateConfig, description: 'Создание материала контента' },
        body: data,
        wrapperFunction() {
            return axios.post<any>(
                `/site/ru/draft/isna-portal-cms/_/service/isnareactcms/content-type`,
                data
            );
        },
    });
};

/**
 * Обновление материала контента
 */
export const updateContentByType = (data: any): Promise<AxiosResponse<any>> => {
    return logFunction({
        config: { ...sectionEditConfig, description: 'Обновление материала контента' },
        body: data,
        wrapperFunction() {
            return axios.put<any>(
                `/site/ru/draft/isna-portal-cms/_/service/isnareactcms/content-type`,
                data
            );
        },
    });
};

/**
 * Создание пустой страницы материала контента
 */
export const createEmptyContentPage = (data: any): Promise<AxiosResponse<any>> => {
    return logFunction({
        config: {
            ...sectionCreateConfig,
            description: 'Создание пустой страницы материала контента',
        },
        body: data,
        wrapperFunction() {
            return axios.post<any>(`/admin/rest-v2/cs/cms/ru/content/content/create`, data);
        },
    });
};

/**
 * Обновление материала контента
 */
export const updateContentPage = (data: any, lang = 'ru'): Promise<AxiosResponse<any>> => {
    return logFunction({
        config: { ...sectionEditConfig, description: 'Обновление материала контента' },
        body: data,
        wrapperFunction() {
            return axios.post<any>(`/admin/rest-v2/cs/cms/${lang}/content/content/update`, data);
        },
    });
};

/**
 * Список материалов родительского элемента (опубликованные и не опубликованные)
 * @param parentId
 * @param from
 * @param size
 * @param lang
 */
export const getContentList = (
    parentId: string,
    from: number,
    size: number,
    lang = 'ru'
): Promise<any> => {
    return logFunction({
        config: {
            ...sectionViewConfig,
            description:
                'Список материалов родительского элемента (опубликованные и не опубликованные)',
        },
        wrapperFunction() {
            return axios.get(`/admin/rest-v2/cs/cms/${lang}/content/content/list`, {
                params: {
                    parentId,
                    expand: 1,
                    from,
                    size,
                },
            });
        },
    });
};

/**
 * Получение данных материала
 * @param id
 * @param lang
 */
export const getContentByIdCMS = (id: string, lang = 'ru'): Promise<any> => {
    return logFunction({
        config: { ...sectionViewConfig, description: 'Получение данных материала' },
        wrapperFunction() {
            return axios.get(`/admin/rest-v2/cs/cms/${lang}/content/content`, {
                params: { id },
            });
        },
    });
};

/**
 * Процессинг и нормализация ссылок
 * @param value
 */
export const processHtml = (value: string): Promise<any> => {
    return logFunction({
        config: { ...sectionEditConfig, description: 'Процессинг и нормализация ссылок' },
        body: { value },
        wrapperFunction() {
            return axios.post(`/site/ru/master/isna-portal-cms/_/service/isnareactcms/helper`, {
                value,
            });
        },
    });
};

/**
 * Список материалов для поиска
 * @param data
 * @param lang
 */
export const getSearchContentList = (data: any, lang = 'ru'): Promise<any> => {
    return logFunction({
        config: { ...sectionViewConfig, description: 'Список материалов для поиска' },
        wrapperFunction() {
            return axios.post(`/admin/rest-v2/cs/cms/${lang}/content/content/query`, data);
        },
    });
};

/**
 * Сохранение медиа файла
 * @param data
 * @param lang
 */
export const createMedia = (data: any, lang = 'ru'): Promise<any> => {
    return logFunction({
        config: { ...sectionCreateConfig, description: 'Сохранение медиа файла' },
        body: data,
        wrapperFunction() {
            return axios.post(`/admin/rest-v2/cs/cms/${lang}/content/content/createMedia`, data, {
                headers: {
                    'Content-Type': 'multipart/form-data',
                },
            });
        },
    });
};

/**
 * Сохранение вложений
 * @param data
 * @param lang
 */
export const createAttachment = (data: any, lang = 'ru'): Promise<any> => {
    return logFunction({
        config: { ...sectionCreateConfig, description: 'Сохранение вложений' },
        body: data,
        wrapperFunction() {
            return axios.post(
                `/admin/rest-v2/cs/cms/${lang}/content/content/createAttachment`,
                data,
                {
                    headers: {
                        'Content-Type': 'multipart/form-data',
                    },
                }
            );
        },
    });
};

/**
 * Сохранение описание вложений (friendly name)
 * @param data
 */
export const saveFileDesc = (data: any): Promise<any> => {
    return logFunction({
        config: {
            section: EventSection.ADM_ACTIONS,
            type: EventType.IMPORT,
            description: 'Сохранение описание вложений (friendly name)'
        },
        body: data,
        wrapperFunction() {
            return axios.post(`/site/ru/draft/isna-portal-cms/_/service/isnareactcms/helper`, data);
        },
    });
};

/**
 * Получение описание вложений контента (friendly name)
 * @param id
 */
export const getFileDescRequest = (id: string): Promise<any> => {
    return logFunction({
        config: {...sectionViewConfig, description: 'Получение описание вложений контента (friendly name)'},
        wrapperFunction() {
            return axios.get(`/site/ru/draft/isna-portal-cms/_/service/isnareactcms/helper`, {
                params: { id, action: 'file' },
            });
        },
    });
};

/**
 * Удаление вложений
 * @param data
 * @param lang
 */
export const deleteAttachment = (data: any, lang = 'ru'): Promise<any> => {
    return logFunction({
        config: {
            section: EventSection.ADM_ACTIONS,
            type: EventType.DELETE,
            ...sectionDeleteConfig,
            description: 'Удаление вложений'
        },
        body: data,
        wrapperFunction() {
            return axios.post(
                `/admin/rest-v2/cs/cms/${lang}/content/content/deleteAttachment`,
                data
            );
        },
    });
};

/**
 * Получение медиафайлов материала
 * @param data
 * @param lang
 */
export const getMediaByContentId = (data: any, lang = 'ru'): Promise<any> => {
    return logFunction({
        config: {...sectionViewConfig, description: 'Получение медиафайлов материала'},
        wrapperFunction() {
            return axios.post(`/admin/rest-v2/cs/cms/${lang}/content/content/selectorQuery`, data);
        },
    });
};

/**
 * Публикация материала
 * @param data
 * @param lang
 */
export const publishContentById = (data: any, lang = 'ru'): Promise<any> => {
    return logFunction({
        config: {...sectionEditConfig, description: 'Публикация материала'},
        body: data,
        wrapperFunction() {
            return axios.post(`/admin/rest-v2/cs/cms/${lang}/content/content/publish`, data);
        },
    });
};

/**
 * Снятие с публикация материала
 * @param data
 * @param lang
 */
export const unpublishContentById = (data: any, lang = 'ru'): Promise<any> => {
    return logFunction({
        config: {...sectionEditConfig, description: 'Снятие с публикация материала'},
        body: data,
        wrapperFunction() {
            return axios.post(`/admin/rest-v2/cs/cms/${lang}/content/content/unpublish`, data);
        },
    });
};

/**
 * Архивация материала
 * @param data
 * @param lang
 */
export const archiveContentById = (data: any, lang = 'ru'): Promise<any> => {
    return logFunction({
        config: {...sectionEditConfig, description: 'Архивация материала'},
        body: data,
        wrapperFunction() {
            return axios.post(
                `/admin/rest-v2/cs/cms/${lang}/content/content/archive/archive`,
                data
            );
        },
    });
};

/**
 * Получение данных о публикации материала
 * @param data
 * @param lang
 */
export const compareContent = (data: any, lang = 'ru'): Promise<any> => {
    return logFunction({
        config: {...sectionEditConfig, description: 'Получение данных о публикации материала'},
        body: data,
        wrapperFunction() {
            return axios.post(`/admin/rest-v2/cs/cms/${lang}/content/content/compare`, data);
        },
    });
};

/**
 * Локализация материала
 * @param data
 * @param lang
 */
export const localizeContent = (data: any, lang = 'ru'): Promise<any> => {
    return logFunction({
        config: {...sectionEditConfig, description: 'Локализация материала'},
        body: data,
        wrapperFunction() {
            return axios.post(`/admin/rest-v2/cs/cms/${lang}/content/content/localize`, data);
        },
    });
};

/**
 * Получение списка версий материала
 * @param data
 * @param lang
 */
export const getVersionsForView = (data: any, lang = 'ru'): Promise<any> => {
    return logFunction({
        config: {...sectionViewConfig, description: 'Получение списка версий материала'},
        wrapperFunction() {
            return axios.post(
                `/admin/rest-v2/cs/cms/${lang}/content/content/getVersionsForView`,
                data
            );
        },
    });
};

/**
 * Откат версии материала
 * @param data
 * @param lang
 */
export const revert = (data: any, lang = 'ru'): Promise<any> => {
    return logFunction({
        body: data,
        config: {
            section: EventSection.ADM_ACTIONS,
            type: EventType.EDIT,
            description: 'Откат версии материала'
        },
        wrapperFunction() {
            return axios.post(`/admin/rest-v2/cs/cms/${lang}/content/content/revert`, data);
        }
    })
};

/**
 * Перемещение материала
 * @param data
 * @param lang
 */
export const move = (data: MoveData, lang = 'ru'): Promise<any> => {
    return axios.post(`/admin/rest-v2/cs/cms/${lang}/content/content/move`, data);
};
